import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, MenuItem, TextField, CircularProgress,
  Alert, Snackbar, LinearProgress, Chip, Grid, Avatar, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, getDoc, addDoc, doc, updateDoc, serverTimestamp, query, orderBy, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { uploadLabFile } from '../services/storageService';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { isTextParam, isBinaryParam, parseRefBound, evaluateParameter, getRefDisplay } from '../utils/parameterEvaluator';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

const PARAM_STATUS = {
  normal:   { label: 'Normal',     color: '#16A34A', bg: '#DCFCE7', icon: '🟢' },
  text:     { label: 'Descriptivo',color: '#6D28D9', bg: '#EDE9FE', icon: '📝' },
  high:     { label: 'Alto ↑',     color: '#DC2626', bg: '#FEE2E2', icon: '🔴' },
  low:      { label: 'Bajo ↓',     color: '#2563EB', bg: '#DBEAFE', icon: '🔵' },
  critical: { label: '⚠ Crítico',  color: '#7F1D1D', bg: '#FEE2E2', icon: '⚠️' },
  unknown:  { label: 'Sin ref.',   color: '#6B7280', bg: '#F3F4F6', icon: '⚪' },
  positive: { label: 'Positivo +', color: '#C62828', bg: '#FCEBEA', icon: '+' },
  negative: { label: 'Negativo −', color: '#16A34A', bg: '#DCFCE7', icon: '−' },
};

const SPECIES_ALIAS = {
  canino: 'perro', felino: 'gato', equino: 'equino', bovino: 'bovino', aviar: 'aviar',
};

const norm = (s) =>
  (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();

const recalcStatus = (p) => evaluateParameter(p.value, p.ref, p.refType);

// Construye las filas de parámetros desde la plantilla del examen (examTypes),
// tomando valores desde lecturas existentes (IA o manuales) cuando existan.
const buildParams = (template, readings, speciesKey) =>
  (template.parameters || []).map((tp, i) => {
    const originKey = (tp.originName || '').trim().toLowerCase();
    const n2 = norm(tp.name);
    const reading = (readings || []).find((r) => {
      const raw = (r.paramName || '').trim().toLowerCase();
      if (originKey && raw === originKey) return true;
      const n1 = norm(r.paramName || '');
      return n1 === n2 || (n1.length > 3 && n2.length > 3 && (n1.includes(n2) || n2.includes(n1)));
    });
    const isText = isTextParam(tp);
    const isBinary = isBinaryParam(tp);
    const refType = isText ? 'none' : (tp.refType || 'minmax');
    const ref = isText ? null : (tp.refRanges?.[speciesKey] || tp.refRanges?.default || null);
    const value = reading?.value || (isText ? (tp.defaultText || '') : '');
    return {
      _idx: i,
      paramName: tp.name,
      value,
      unit: isText ? '' : (reading?.unit || tp.unit || ''),
      metodo: tp.metodo || '',
      ref,
      refType,
      refDisplay: isText ? '—' : getRefDisplay(ref, refType),
      category: tp.category || 'General',
      isText,
      isBinary,
      status: isText ? 'text' : recalcStatus({ value, ref, refType }),
    };
  });

export default function UploadResult() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { filterByBranch } = useBranch();

  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(searchParams.get('orderId') || '');
  const [selectedExamTypeId, setSelectedExamTypeId] = useState(searchParams.get('examTypeId') || '');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | uploading | processing | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Plantilla de parámetros (informe) editable
  const [examTemplate, setExamTemplate] = useState(null);
  const [params, setParams] = useState([]);
  const [observations, setObservations] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const preId = searchParams.get('orderId');
    getDocs(query(collection(db, 'labOrders'), orderBy('createdAt', 'desc')))
      .then((snap) => {
        const all = filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true });
        const filtered = all.filter((o) => {
          if (o.status === 'reported') return false;
          const exams = o.examsRequested || [];
          if (exams.length === 0) return o.status !== 'reported';
          const examResults = o.examResults || {};
          return exams.some((e) => !examResults[e.examTypeId]?.resultId);
        });
        // If URL orderId is not in filtered list (status edge case), fetch and add it directly
        if (preId && !filtered.find((o) => o.id === preId)) {
          const fromAll = all.find((o) => o.id === preId);
          if (fromAll) {
            filtered.unshift(fromAll);
          } else {
            getDoc(doc(db, 'labOrders', preId))
              .then((d) => { if (d.exists()) setOrders((prev) => [{ id: d.id, ...d.data() }, ...prev]); })
              .catch(() => {});
          }
        }
        setOrders(filtered);
      })
      .catch((err) => console.error('[UploadResult] Error loading orders:', err));
  }, []);

  const handleFile = useCallback((f) => {
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type)) {
      setSnackbar({ open: true, message: 'Solo se aceptan archivos PDF, JPG o PNG.', severity: 'warning' });
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'El archivo no debe superar los 20 MB.', severity: 'warning' });
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setPhase('idle');
    setErrorMsg('');
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  }, [handleFile]);

  const handleProcess = async () => {
    if (!selectedOrderId) return setSnackbar({ open: true, message: 'Selecciona una orden primero.', severity: 'warning' });
    if (!file) return setSnackbar({ open: true, message: 'Sube un archivo primero.', severity: 'warning' });

    const order = orders.find((o) => o.id === selectedOrderId);
    if (!order) return;

    const exam = selectedExam;
    if (!exam?.examTypeId) return setSnackbar({ open: true, message: 'Selecciona el examen a procesar.', severity: 'warning' });

    // Check if this exam already has a result
    const existingResultId = order.examResults?.[exam.examTypeId]?.resultId;
    if (existingResultId) {
      return setSnackbar({ open: true, message: 'Este examen ya tiene un resultado subido.', severity: 'warning' });
    }

    setPhase('uploading');
    setUploadProgress(0);
    setErrorMsg('');

    try {
      // 1. Create result document for this specific exam
      const resultRef = await addDoc(collection(db, 'labResults'), {
        orderId: selectedOrderId,
        examTypeId: exam.examTypeId,
        examTypeName: exam.examTypeName,
        patientId: order.patientId,
        patientName: order.patientName,
        patientSpecies: order.patientSpecies,
        status: 'processing',
        uploadedBy: currentUser?.uid || '',
        uploadedAt: serverTimestamp(),
      });

      // 2. Upload file to Storage
      const fileType = file.type === 'application/pdf' ? 'pdf' : 'jpg';
      const { url, path } = await uploadLabFile(file, resultRef.id, setUploadProgress);

      // 3. Update result with file info
      await updateDoc(doc(db, 'labResults', resultRef.id), {
        originalFilePath: path,
        originalFileUrl: url,
        originalFileType: fileType,
      });

      // 4. Link result to order per-exam (examResults map) + legacy resultId
      const examResultsUpdate = {
        [`examResults.${exam.examTypeId}`]: { resultId: resultRef.id, status: 'processing' },
        resultId: resultRef.id, // keep legacy field pointing to latest
        status: 'processing',
      };
      await updateDoc(doc(db, 'labOrders', selectedOrderId), examResultsUpdate);

      // 5. Audit log
      await addDoc(collection(db, 'labAuditLog'), {
        orderId: selectedOrderId,
        resultId: resultRef.id,
        examTypeId: exam.examTypeId,
        action: 'file_uploaded',
        performedBy: currentUser?.uid || '',
        performedAt: serverTimestamp(),
        details: `Archivo ${file.name} subido para examen "${exam.examTypeName}". Tipo: ${fileType}`,
      });

      setPhase('processing');

      // 6. Call Cloud Function
      const processLabResult = httpsCallable(functions, 'processLabResult');
      await processLabResult({
        resultId: resultRef.id,
        filePath: path,
        fileType,
        examTypeId: exam.examTypeId,
        patientSpecies: order.patientSpecies || 'perro',
        orderId: selectedOrderId,
        orderNumber: order.orderNumber || '',
      });

      // 7. Update per-exam status → processing (se confirmará al guardar)
      await updateDoc(doc(db, 'labOrders', selectedOrderId), {
        [`examResults.${exam.examTypeId}.status`]: 'processing',
      });

      // 8. Leer parámetros extraídos por la IA y cargarlos en la plantilla editable
      const readingsSnap = await getDocs(query(
        collection(db, 'labReadings'),
        where('orderId', '==', selectedOrderId),
        where('examTypeId', '==', exam.examTypeId),
      ));
      const readings = readingsSnap.docs.flatMap((d) => d.data().parameters || []);

      if (examTemplate) {
        const speciesRaw = (order.patientSpecies || 'perro').toLowerCase();
        const speciesKey = SPECIES_ALIAS[speciesRaw] || speciesRaw;
        setParams(buildParams(examTemplate, readings, speciesKey));
      }

      setPhase('done');
      setSnackbar({
        open: true,
        message: `${readings.length} parámetro(s) extraídos por IA. Revisa los valores en la plantilla y guarda.`,
        severity: 'success',
      });

    } catch (err) {
      setPhase('error');
      setErrorMsg(err.message || 'Error al procesar el archivo.');
    }
  };

  // Edición manual de un valor/unidad en la plantilla
  const handleValueChange = useCallback((idx, field, value) => {
    setParams((prev) => prev.map((p) => {
      if (p._idx !== idx) return p;
      const updated = { ...p, [field]: value };
      if (field === 'value' && !p.isText) updated.status = recalcStatus(updated);
      return updated;
    }));
  }, []);

  // Guarda los parámetros (manuales o revisados de IA) como lecturas + resultado
  const handleSave = async () => {
    const order = orders.find((o) => o.id === selectedOrderId);
    const exam = selectedExam;
    if (!order || !exam?.examTypeId) {
      return setSnackbar({ open: true, message: 'Selecciona una orden y examen primero.', severity: 'warning' });
    }
    if (params.length === 0) {
      return setSnackbar({ open: true, message: 'No hay parámetros en la plantilla de este examen.', severity: 'warning' });
    }
    if (!params.some((p) => String(p.value).trim() !== '')) {
      return setSnackbar({ open: true, message: 'Ingresa al menos un valor antes de guardar.', severity: 'warning' });
    }

    setSaving(true);
    try {
      const readingParams = params.map((p) => ({
        paramName: p.paramName,
        value: String(p.value ?? ''),
        unit: p.unit || '',
      }));

      // 1. labReadings: actualizar existente para este examen o crear nuevo
      const readingsSnap = await getDocs(query(
        collection(db, 'labReadings'),
        where('orderId', '==', selectedOrderId),
        where('examTypeId', '==', exam.examTypeId),
      ));
      if (!readingsSnap.empty) {
        await updateDoc(doc(db, 'labReadings', readingsSnap.docs[0].id), {
          parameters: readingParams,
          source: file ? 'ai_reviewed' : 'manual',
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'labReadings'), {
          patientId: order.patientId || '',
          orderId: selectedOrderId,
          orderNumber: order.orderNumber || '',
          examTypeId: exam.examTypeId,
          examTypeName: exam.examTypeName || '',
          parameters: readingParams,
          source: file ? 'ai_reviewed' : 'manual',
          extractedAt: serverTimestamp(),
        });
      }

      // 2. labResults: asegurar un documento de resultado para este examen
      const resultsSnap = await getDocs(query(
        collection(db, 'labResults'),
        where('orderId', '==', selectedOrderId),
        where('examTypeId', '==', exam.examTypeId),
      ));
      let resultId;
      const observationsClean = observations || '';
      if (!resultsSnap.empty) {
        resultId = resultsSnap.docs[0].id;
        await updateDoc(doc(db, 'labResults', resultId), {
          status: 'pending_review',
          observations: observationsClean,
          updatedAt: serverTimestamp(),
        });
      } else {
        const resultRef = await addDoc(collection(db, 'labResults'), {
          orderId: selectedOrderId,
          examTypeId: exam.examTypeId,
          examTypeName: exam.examTypeName || '',
          patientId: order.patientId || '',
          patientName: order.patientName || '',
          patientSpecies: order.patientSpecies || '',
          status: 'pending_review',
          observations: observationsClean,
          source: file ? 'ai_reviewed' : 'manual',
          uploadedBy: currentUser?.uid || '',
          uploadedAt: serverTimestamp(),
        });
        resultId = resultRef.id;
      }

      // 3. Vincular resultado a la orden por examen + estado global
      const allExams = order.examsRequested || [];
      const currentExamResults = {
        ...(order.examResults || {}),
        [exam.examTypeId]: { resultId, status: 'pending_review' },
      };
      const allDone = allExams.every((e) => currentExamResults[e.examTypeId]?.resultId);
      await updateDoc(doc(db, 'labOrders', selectedOrderId), {
        [`examResults.${exam.examTypeId}`]: { resultId, status: 'pending_review' },
        resultId,
        status: allDone ? 'pending_review' : 'processing',
      });

      // 4. Auditoría
      await addDoc(collection(db, 'labAuditLog'), {
        orderId: selectedOrderId,
        resultId,
        examTypeId: exam.examTypeId,
        action: file ? 'result_reviewed' : 'result_manual_entry',
        performedBy: currentUser?.uid || '',
        performedAt: serverTimestamp(),
        details: `Parámetros guardados (${readingParams.length}) para "${exam.examTypeName}" vía ${file ? 'revisión IA' : 'ingreso manual'}.`,
      });

      setSnackbar({ open: true, message: 'Parámetros guardados. Redirigiendo a generación de informe…', severity: 'success' });
      setTimeout(() => navigate(`/lab/report/${selectedOrderId}`), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Error al guardar los parámetros.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectedOrder = orders.find((o) => o.id === selectedOrderId);

  // Auto-select first exam if no examTypeId in URL
  useEffect(() => {
    if (selectedOrder && !selectedExamTypeId) {
      const firstId = selectedOrder.examsRequested?.[0]?.examTypeId || '';
      setSelectedExamTypeId(firstId);
    }
  }, [selectedOrder, selectedExamTypeId]);

  const selectedExam = selectedOrder?.examsRequested?.find(
    (e) => e.examTypeId === selectedExamTypeId
  ) || selectedOrder?.examsRequested?.[0];

  const selectedExamId = selectedExam?.examTypeId;

  // Cargar plantilla del examen + lecturas existentes en la plantilla editable
  useEffect(() => {
    if (!selectedOrderId || !selectedExamId) {
      setExamTemplate(null);
      setParams([]);
      setObservations('');
      return;
    }
    let cancelled = false;
    (async () => {
      setTemplateLoading(true);
      try {
        const tplDoc = await getDoc(doc(db, 'examTypes', selectedExamId));
        const template = tplDoc.exists() ? { id: tplDoc.id, ...tplDoc.data() } : { parameters: [] };

        const order = orders.find((o) => o.id === selectedOrderId);
        const speciesRaw = (order?.patientSpecies || 'perro').toLowerCase();
        const speciesKey = SPECIES_ALIAS[speciesRaw] || speciesRaw;

        const readingsSnap = await getDocs(query(
          collection(db, 'labReadings'),
          where('orderId', '==', selectedOrderId),
          where('examTypeId', '==', selectedExamId),
        ));
        const readings = readingsSnap.docs.flatMap((d) => d.data().parameters || []);

        const resultsSnap = await getDocs(query(
          collection(db, 'labResults'),
          where('orderId', '==', selectedOrderId),
          where('examTypeId', '==', selectedExamId),
        ));
        const existingObs = resultsSnap.empty ? '' : (resultsSnap.docs[0].data().observations || '');

        if (cancelled) return;
        setExamTemplate(template);
        setParams(buildParams(template, readings, speciesKey));
        setObservations(existingObs);
      } catch (err) {
        console.error('[UploadResult] Error cargando plantilla:', err);
        if (!cancelled) { setExamTemplate({ parameters: [] }); setParams([]); }
      } finally {
        if (!cancelled) setTemplateLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId, selectedExamId]);

  const grouped = params.reduce((acc, p) => {
    const cat = p.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});
  const criticalCount = params.filter((p) => p.status === 'critical').length;

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab/orders')}
          sx={{ color: LAB_PRIMARY, fontWeight: 600 }}>Volver</Button>
        <CloudUploadIcon sx={{ color: LAB_PRIMARY }} />
        <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Subir Resultado de Examen</Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Left: order selector + upload zone */}
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              1. Seleccionar orden
            </Typography>
            <TextField
              select fullWidth size="small" label="Orden de examen *"
              value={selectedOrderId}
              onChange={(e) => { setSelectedOrderId(e.target.value); setSelectedExamTypeId(''); }}>
              <MenuItem value="">Seleccionar…</MenuItem>
              {orders.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.orderNumber}
                </MenuItem>
              ))}
            </TextField>

            {/* Exam selector — shown when order has multiple exams */}
            {selectedOrder && (selectedOrder.examsRequested?.length || 0) > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  SELECCIONAR EXAMEN
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedOrder.examsRequested.map((exam) => {
                    const examResult = selectedOrder.examResults?.[exam.examTypeId];
                    const isSelected = selectedExamTypeId === exam.examTypeId;
                    const hasDone   = !!examResult?.resultId;
                    return (
                      <Box
                        key={exam.examTypeId}
                        onClick={() => !hasDone && setSelectedExamTypeId(exam.examTypeId)}
                        sx={{
                          p: 1.5, borderRadius: 2, border: '2px solid',
                          borderColor: isSelected ? LAB_PRIMARY : hasDone ? '#BBF7D0' : '#E2E8F0',
                          bgcolor: isSelected ? '#EEF2FF' : hasDone ? '#F0FDF4' : '#FAFAFA',
                          cursor: hasDone ? 'default' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          '&:hover': !hasDone ? { borderColor: LAB_PRIMARY } : {},
                        }}
                      >
                        <Typography variant="body2" fontWeight={isSelected ? 700 : 500} color={isSelected ? LAB_PRIMARY : 'text.primary'}>
                          {exam.examTypeName}
                        </Typography>
                        {hasDone
                          ? <Chip label="Subido" size="small" sx={{ bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                          : isSelected
                          ? <Chip label="Seleccionado" size="small" sx={{ bgcolor: LAB_PRIMARY, color: '#fff', fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                          : <Chip label="Pendiente" size="small" sx={{ bgcolor: '#FEF3C7', color: '#D97706', fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                        }
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {selectedExam && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
                <Typography variant="body2" fontWeight={700} color={LAB_PRIMARY}>
                  {selectedOrder.patientName}
                  {selectedOrder.priority === 'urgent' && (
                    <Chip label="URGENTE" size="small"
                      sx={{ ml: 1, bgcolor: '#FEE2E2', color: '#DC2626', fontSize: '0.6rem', fontWeight: 700, height: 18 }} />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedOrder.patientSpecies} · <strong>{selectedExam.examTypeName}</strong>
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              2. Cargar archivo (PDF o JPG)
            </Typography>

            {/* Drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={onDrop}
              onClick={() => !file && fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${isDragOver ? LAB_TEAL : '#CBD5E1'}`,
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                cursor: file ? 'default' : 'pointer',
                bgcolor: isDragOver ? '#F0FDFA' : file ? '#F8FAFC' : '#FAFAFA',
                transition: 'all 0.2s',
                '&:hover': !file ? { borderColor: LAB_TEAL, bgcolor: '#F0FDFA' } : {},
              }}>
              {file ? (
                <Box>
                  <Avatar sx={{ bgcolor: file.type === 'application/pdf' ? '#EF4444' : LAB_TEAL, mx: 'auto', mb: 1, width: 48, height: 48 }}>
                    {file.type === 'application/pdf' ? <PictureAsPdfIcon /> : <ImageIcon />}
                  </Avatar>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 220, mx: 'auto' }}>{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setFile(null); setPreviewUrl(null); setPhase('idle'); }}>
                      Cambiar archivo
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Arrastra el PDF o imagen aquí
                  </Typography>
                  <Typography variant="caption" color="text.disabled">o haz clic para seleccionar</Typography>
                  <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 0.5 }}>
                    PDF, JPG, PNG · Máx. 20 MB
                  </Typography>
                </Box>
              )}
            </Box>
            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files?.[0])} />

            {/* Progress */}
            {(phase === 'uploading' || phase === 'processing') && (
              <Box sx={{ mt: 2 }}>
                {phase === 'uploading' && (
                  <>
                    <Typography variant="caption" color={LAB_PRIMARY} fontWeight={600}>
                      Subiendo archivo… {uploadProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 0.5, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: LAB_TEAL } }} />
                  </>
                )}
                {phase === 'processing' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#EFF6FF', borderRadius: 2, border: '1px solid #BFDBFE' }}>
                    <CircularProgress size={24} sx={{ color: LAB_PRIMARY }} />
                    <Box>
                      <Typography variant="body2" fontWeight={700} color={LAB_PRIMARY}>
                        Procesando con IA…
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        OCR + Claude extrayendo parámetros. Puede tomar 15–30 seg.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {phase === 'done' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
                <CheckCircleIcon sx={{ color: '#16A34A' }} />
                <Typography variant="body2" fontWeight={700} color="#16A34A">
                  Parámetros cargados en la plantilla. Revísalos y guarda.
                </Typography>
              </Box>
            )}

            {phase === 'error' && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{errorMsg}</Alert>
            )}

            <Button
              fullWidth variant="contained"
              onClick={handleProcess}
              disabled={!file || !selectedOrderId || phase === 'uploading' || phase === 'processing'}
              startIcon={<AutoFixHighIcon />}
              sx={{ mt: 2.5, bgcolor: LAB_PRIMARY, fontWeight: 700, py: 1.2, '&:hover': { bgcolor: '#0F2A45' }, borderRadius: 2 }}>
              {phase === 'processing' ? 'Procesando con IA…' : 'Procesar con IA'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Opcional: la IA rellenará la plantilla. También puedes llenarla manualmente.
            </Typography>
          </Paper>

          {/* File preview (para transcribir manualmente) */}
          {previewUrl && (
            <Paper elevation={0} sx={{ mt: 2, p: 0, borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid #E2E8F0', bgcolor: LAB_PRIMARY }}>
                <Typography variant="subtitle2" fontWeight={700} color="#fff">
                  Vista previa del archivo
                </Typography>
              </Box>
              {file?.type === 'application/pdf' ? (
                <iframe src={previewUrl} style={{ width: '100%', height: '55vh', border: 'none' }} title="preview" />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '55vh', bgcolor: '#F4F6F8', overflow: 'auto', p: 2 }}>
                  <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Right: editable report template */}
        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>
                  3. Plantilla del informe
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedExam ? selectedExam.examTypeName : 'Selecciona una orden y examen'}
                  {examTemplate?.tipoMuestra ? ` · Muestra: ${examTemplate.tipoMuestra}` : ''}
                </Typography>
              </Box>
              {params.length > 0 && (
                <Chip label={`${params.length} parámetros`} size="small"
                  sx={{ bgcolor: '#EFF6FF', color: LAB_PRIMARY, fontWeight: 700 }} />
              )}
            </Box>

            {criticalCount > 0 && (
              <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
                <strong>{criticalCount} parámetro(s) con valor CRÍTICO</strong> — revisa antes de guardar.
              </Alert>
            )}

            {templateLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : !selectedExam ? (
              <Alert severity="info">Selecciona una orden y un examen para cargar su plantilla de parámetros.</Alert>
            ) : params.length === 0 ? (
              <Alert severity="warning">
                Este examen no tiene parámetros configurados en su plantilla. Configúralos en el Catálogo de Exámenes.
              </Alert>
            ) : (
              <>
                {Object.entries(grouped).map(([category, catParams]) => (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Box sx={{ bgcolor: LAB_TEAL, px: 2, py: 0.5, borderRadius: '8px 8px 0 0' }}>
                      <Typography variant="caption" fontWeight={700} color="#fff"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        {category}
                      </Typography>
                    </Box>
                    <TableContainer sx={{ border: '1px solid #E2E8F0', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY }}>Parámetro</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 100 }}>Valor</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 80 }}>Unidad</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 110 }}>Referencia</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 90 }}>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {catParams.map((p) => {
                            const st = PARAM_STATUS[p.status] || PARAM_STATUS.unknown;
                            const isCritical = p.status === 'critical';
                            const isAbnormal = ['high', 'low', 'critical', 'positive'].includes(p.status);
                            return (
                              <TableRow key={p._idx}
                                sx={{ bgcolor: isCritical ? '#FFF5F5' : isAbnormal ? '#FFF5F5' : 'inherit', '&:hover': { bgcolor: '#F8FAFC' } }}>
                                <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: isCritical ? '#991B1B' : 'inherit' }}>
                                  {p.paramName}
                                  {p.metodo && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                                      {p.metodo}
                                    </Typography>
                                  )}
                                </TableCell>
                                {p.isText ? (
                                  <TableCell colSpan={3}>
                                    <TextField fullWidth size="small" multiline minRows={2} value={p.value}
                                      onChange={(e) => handleValueChange(p._idx, 'value', e.target.value)}
                                      placeholder="Describe la morfología observada…"
                                      inputProps={{ style: { fontSize: '0.78rem' } }} />
                                  </TableCell>
                                ) : p.isBinary ? (
                                  <>
                                    <TableCell>
                                      <TextField select size="small" value={p.value}
                                        onChange={(e) => handleValueChange(p._idx, 'value', e.target.value)}
                                        inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px' } }}
                                        sx={{ width: 100 }}>
                                        <MenuItem value=""><em>—</em></MenuItem>
                                        <MenuItem value="Negativo">Negativo</MenuItem>
                                        <MenuItem value="Positivo">Positivo</MenuItem>
                                      </TextField>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.unit}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.refDisplay}</TableCell>
                                  </>
                                ) : (
                                  <>
                                    <TableCell>
                                      <TextField size="small" value={p.value}
                                        onChange={(e) => handleValueChange(p._idx, 'value', e.target.value)}
                                        placeholder="—"
                                        inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px' } }}
                                        sx={{ width: 90 }} />
                                    </TableCell>
                                    <TableCell>
                                      <TextField size="small" value={p.unit}
                                        onChange={(e) => handleValueChange(p._idx, 'unit', e.target.value)}
                                        inputProps={{ style: { fontSize: '0.75rem', padding: '4px 4px' } }}
                                        sx={{ width: 70 }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.refDisplay}</TableCell>
                                  </>
                                )}
                                <TableCell>
                                  <Chip label={`${st.icon} ${st.label}`} size="small"
                                    sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.62rem', height: 20 }} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY} mb={1}>
                  Observaciones / Interpretación clínica
                </Typography>
                <TextField
                  fullWidth multiline rows={3} size="small"
                  placeholder="Observaciones clínicas, interpretación o comentarios adicionales…"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                />

                <Button
                  fullWidth variant="contained"
                  onClick={handleSave}
                  disabled={saving || phase === 'uploading' || phase === 'processing'}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  sx={{ mt: 2, bgcolor: LAB_TEAL, fontWeight: 700, py: 1.2, '&:hover': { bgcolor: '#229984' }, borderRadius: 2 }}>
                  {saving ? 'Guardando…' : 'Guardar y continuar a informe'}
                </Button>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
